// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TySupportTicket } from "@/types";
import SupportTicketCard from "@/UI/cards/SupportTicketCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FormSupportTicketPopup from "@/UI/popups/FormSupportTicketPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function SupportScreen() {
    const t = useTranslation();
    const { addComp, removeComp } = usePopup();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ tickets: TySupportTicket[]; total: number }>>("/support/", {
        query: { limit: 100 },
    });

    const tickets = data?.data?.tickets ?? [];

    // --- Delete ----------------------------------------------------------------------------------

    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: deleteData, invoke: invokeDelete } = useFetch<TyApiResponse<Record<string, never>>>(
        `/support/${deletingId ?? "x"}`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!deletingId) return;
        invokeDelete();
    }, [deletingId]);

    useEffect(() => {
        if (!deleteData?.success) return;
        setDeletingId(null);
        invoke();
    }, [deleteData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleAdd = () => {
        addComp({
            name: "form-support-ticket",
            Component: FormSupportTicketPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
            details: { onSuccess: invoke },
        });
    };

    const handleDeletePrompt = (ticket: TySupportTicket) => {
        removeComp("confirm-delete-ticket");
        addComp({
            name: "confirm-delete-ticket",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.support.deleteMessage,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.support.deleteConfirm,
                onConfirm: () => setDeletingId(ticket._id),
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="support-screen">
            <ActionsBar title={t.support.title} addLabel={t.support.addLabel} onAdd={handleAdd} />
            <div className="support-screen-content">
                <div className="support-screen-note">
                    <img src="/icons/info-circle-icon.svg" alt="" width={18} height={18} />
                    <p>{t.support.infoNote}</p>
                </div>

                {loading && (
                    <div className="support-screen-grid">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="support-screen-skeleton" />
                        ))}
                    </div>
                )}
                {error && <p className="support-screen-message support-screen-message--error">{error}</p>}

                {!loading && !error && tickets.length === 0 && (
                    <div className="support-screen-empty">
                        <div className="support-screen-empty-icon">
                            <img src="/icons/side-bar/support-icon-dark.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="support-screen-empty-text">
                            <p className="support-screen-empty-title">{t.support.emptyTitle}</p>
                            <p className="support-screen-empty-subtitle">{t.support.emptySubtitle}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && tickets.length > 0 && (
                    <div className="support-screen-grid">
                        {tickets.map((ticket) => (
                            <SupportTicketCard key={ticket._id} ticket={ticket} onDelete={() => handleDeletePrompt(ticket)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
